/**
 * Statistics, through the one network gateway. Shapes come from the shared contract (P2).
 *
 * Every statistic accepts the SAME dimension filters (`statsShape` of the contract), so they are
 * built here once: a screen that adds a filter adds it in one place and every block of that
 * screen asks the API the same question.
 */
import type {
  AgentMatrixResponseType,
  AgentShareResponseType,
  BreakdownResponseType,
  OverviewResponseType,
  ProspectsResponseType,
  TerminalMatrixResponseType,
  TimeseriesResponseType,
  TopShipsResponseType,
} from '@thongketau/shared/contract/stats';

import type { Period } from '../utils/period.js';
import { apiGet, type Query } from './httpClient.js';

export type Overview = OverviewResponseType;
export type Timeseries = TimeseriesResponseType;
export type AgentShare = AgentShareResponseType;
export type AgentMatrix = AgentMatrixResponseType;
/** Ma trận NGƯỢC: dòng là cảng, khối cột là đại lý (việc 36). */
export type TerminalMatrix = TerminalMatrixResponseType;
export type Breakdown = BreakdownResponseType;
export type TopShips = TopShipsResponseType;
export type Prospects = ProspectsResponseType;

/** Mọi thống kê đếm TÀU (việc 43): `ships` là số CHUYẾN, không có chỉ tiêu "lượt". */
export type ShareMetric = 'ships' | 'dwt' | 'tons';
/** Thước đo của màn Thị phần đại lý (việc 39) — nay trùng `ShareMetric`. */
export type AgentMetric = 'ships' | 'dwt' | 'tons';
export type Granularity = 'day' | 'week' | 'month' | 'quarter' | 'year';
export type BreakdownDimension = Breakdown['by'];

/** The dimension filters every statistics screen shares (contract `statsShape`). */
export interface StatsFilters {
  readonly flagScope?: string;
  readonly cargoGroup?: readonly string[];
}

export function statsQuery(period: Period, filters: StatsFilters): Query {
  return {
    dateFrom: period.from,
    dateTo: period.to,
    flagScope: filters.flagScope,
    cargoGroup: filters.cargoGroup,
  };
}

/* ---------- overview ---------- */

export const overviewKey = (from: string, to: string, compare?: 'previous' | 'yoy') =>
  ['stats', 'overview', from, to, compare ?? 'none'] as const;

export const fetchOverview = (from: string, to: string, compare?: 'previous' | 'yoy') =>
  apiGet<Overview>('/api/stats/overview', { query: { dateFrom: from, dateTo: to, compare } });

/* ---------- thống kê chung ---------- */

export interface TimeseriesOptions {
  readonly granularity: Granularity;
  readonly splitBy: 'movement' | 'cargoGroup' | 'none';
}

const timeseriesParams = (period: Period, filters: StatsFilters, options: TimeseriesOptions) => ({
  ...statsQuery(period, filters),
  granularity: options.granularity,
  splitBy: options.splitBy,
});

export const timeseriesKey = (period: Period, filters: StatsFilters, options: TimeseriesOptions) =>
  ['stats', 'timeseries', timeseriesParams(period, filters, options)] as const;

export const fetchTimeseries = (
  period: Period,
  filters: StatsFilters,
  options: TimeseriesOptions,
) =>
  apiGet<Timeseries>('/api/stats/timeseries', {
    query: timeseriesParams(period, filters, options),
  });

const breakdownParams = (period: Period, filters: StatsFilters, by: BreakdownDimension) => ({
  ...statsQuery(period, filters),
  by,
});

export const breakdownKey = (period: Period, filters: StatsFilters, by: BreakdownDimension) =>
  ['stats', 'breakdown', breakdownParams(period, filters, by)] as const;

export const fetchBreakdown = (period: Period, filters: StatsFilters, by: BreakdownDimension) =>
  apiGet<Breakdown>('/api/stats/breakdown', { query: breakdownParams(period, filters, by) });

const topShipsParams = (period: Period, filters: StatsFilters, rank: ShareMetric) => ({
  ...statsQuery(period, filters),
  rank,
});

export const topShipsKey = (period: Period, filters: StatsFilters, rank: ShareMetric) =>
  ['stats', 'top-ships', topShipsParams(period, filters, rank)] as const;

export const fetchTopShips = (period: Period, filters: StatsFilters, rank: ShareMetric) =>
  apiGet<TopShips>('/api/stats/top-ships', { query: topShipsParams(period, filters, rank) });

/* ---------- thị phần đại lý ---------- */

export interface AgentShareOptions {
  readonly terminalGroup?: string;
  /**
   * Việc 33 — cảng để CHỌN TÊN đại lý: chỉ đại lý có tàu ở các cảng đang theo dõi mới lên bảng,
   * còn số của họ vẫn đếm trên mọi cảng (không gửi `terminalGroup`/`terminalId`).
   */
  readonly presentTerminalGroup?: string;
  readonly presentTerminalIds?: readonly number[];
  /** D6: "Tự túc" is a real agent of the source and is KEPT by default. */
  readonly excludeSelfHandled: boolean;
}

const agentShareParams = (period: Period, filters: StatsFilters, options: AgentShareOptions) => ({
  ...statsQuery(period, filters),
  terminalGroup: options.terminalGroup,
  presentTerminalGroup: options.presentTerminalIds?.length
    ? undefined
    : options.presentTerminalGroup,
  presentTerminalId: options.presentTerminalIds?.length
    ? options.presentTerminalIds.map(String)
    : undefined,
  excludeSelfHandled: options.excludeSelfHandled,
});

export const agentShareKey = (period: Period, filters: StatsFilters, options: AgentShareOptions) =>
  ['stats', 'agent-share', agentShareParams(period, filters, options)] as const;

export const fetchAgentShare = (
  period: Period,
  filters: StatsFilters,
  options: AgentShareOptions,
) =>
  apiGet<AgentShare>('/api/stats/agent-share', {
    query: agentShareParams(period, filters, options),
  });

export interface AgentMatrixOptions extends AgentShareOptions {
  /** Nhóm cảng mặc định — chỉ gửi khi người đọc CHƯA tự chọn cảng theo dõi (việc 33). */
  readonly terminalGroup?: string;
  /** Cảng người đọc tự chọn; có danh sách này thì nó thắng nhóm cảng. */
  readonly terminalIds?: readonly number[];
  readonly metric: ShareMetric;
  /** The ticked months (`YYYY-MM`) — the matrix's own tick row, not the bar's period. */
  readonly months: readonly string[];
}

const agentMatrixParams = (period: Period, filters: StatsFilters, options: AgentMatrixOptions) => ({
  ...statsQuery(period, filters),
  terminalGroup: options.terminalIds?.length ? undefined : options.terminalGroup,
  terminalId: options.terminalIds?.length ? options.terminalIds.map(String) : undefined,
  excludeSelfHandled: options.excludeSelfHandled,
  metric: options.metric,
  months: options.months,
});

export const agentMatrixKey = (
  period: Period,
  filters: StatsFilters,
  options: AgentMatrixOptions,
) => ['stats', 'agent-terminal-matrix', agentMatrixParams(period, filters, options)] as const;

export const fetchAgentMatrix = (
  period: Period,
  filters: StatsFilters,
  options: AgentMatrixOptions,
) =>
  apiGet<AgentMatrix>('/api/stats/agent-terminal-matrix', {
    query: agentMatrixParams(period, filters, options),
  });

/* ---------- ma trận ngược: cảng × đại lý (việc 36) ---------- */

export interface TerminalMatrixOptions {
  readonly excludeSelfHandled: boolean;
  readonly metric: ShareMetric;
  readonly months: readonly string[];
  /**
   * Đại lý làm CỘT. Rỗng ⇒ API tự lấy top của cửa sổ tháng và nói ra trong `agents` — màn mở
   * lần đầu chưa biết đại lý nào đáng xem.
   *
   * 🚨 Đi dưới tên `columnAgent`: `agent` đã là chiều LỌC của mọi thống kê, dùng lại tên ấy thì
   * chọn cột hoá ra lọc mất cả bảng.
   */
  readonly agents: readonly string[];
}

const terminalMatrixParams = (
  period: Period,
  filters: StatsFilters,
  options: TerminalMatrixOptions,
) => ({
  ...statsQuery(period, filters),
  excludeSelfHandled: options.excludeSelfHandled,
  metric: options.metric,
  months: options.months,
  columnAgent: options.agents.length > 0 ? options.agents : undefined,
});

export const terminalMatrixKey = (
  period: Period,
  filters: StatsFilters,
  options: TerminalMatrixOptions,
) => ['stats', 'terminal-agent-matrix', terminalMatrixParams(period, filters, options)] as const;

export const fetchTerminalMatrix = (
  period: Period,
  filters: StatsFilters,
  options: TerminalMatrixOptions,
) =>
  apiGet<TerminalMatrix>('/api/stats/terminal-agent-matrix', {
    query: terminalMatrixParams(period, filters, options),
  });

/* ---------- tàu đi cảng khác ---------- */

const prospectsParams = (period: Period, filters: StatsFilters, terminalGroup: string) => ({
  ...statsQuery(period, filters),
  terminalGroup,
});

export const prospectsKey = (period: Period, filters: StatsFilters, terminalGroup: string) =>
  ['stats', 'prospects', prospectsParams(period, filters, terminalGroup)] as const;

export const fetchProspects = (period: Period, filters: StatsFilters, terminalGroup: string) =>
  apiGet<Prospects>('/api/stats/prospects', {
    query: prospectsParams(period, filters, terminalGroup),
  });
